/** @jsxImportSource @emotion/react */
import { EuiEmptyPrompt, EuiLoadingLogo } from "@inscopix/ideas-eui";
import { useEffect, useState } from "react";
import { CallOutError } from "components/CallOutError/CallOutError";
import { FileFormat } from "types/FileFormats";
import { useFabricImage } from "../ModalToolCroppingFrameParam/useFabricImage";
import { RoiEditor, RoiEditorProps } from "../../RoiEditor/RoiEditor";
import { RoiEditorProvider } from "../../RoiEditor/RoiEditorProvider";
import { ToolRoiFrameParam } from "../ToolParamsGrid.types";
import { DIMENSIONAL_METADATA_KEYS } from "types/MetadataMap";
import { isNullish } from "utils/isNullish";
import { css } from "@emotion/react";
import { SelectedSourceFile } from "./ModalToolRoiFrameParamBase";

export interface ModalToolRoiFrameParamWithFileProps
  extends Pick<RoiEditorProps, "onAccept" | "onCancel" | "initialShapes"> {
  file: SelectedSourceFile;
  previewType: {
    fileFormat: FileFormat["id"];
    key: ToolRoiFrameParam["type"]["source_file"]["data"]["key"];
  };
  readOnly?: boolean;
  roiSettings: ToolRoiFrameParam["type"]["roi_settings"];
}

export const ModalToolRoiFrameParamWithFile = ({
  file,
  onAccept,
  initialShapes,
  previewType,
  onCancel,
  readOnly,
  roiSettings,
}: ModalToolRoiFrameParamWithFileProps) => {
  const [scaleFactor, setScaleFactor] = useState<{ x: number; y: number }>();
  const [loadingScaling, setLoadingScaling] = useState(true);

  const { loading, image, error } = useFabricImage(
    file.id,
    previewType.fileFormat,
    previewType.key,
  );

  /**
   * Parse source file metadata and determine preview scaling
   */
  useEffect(() => {
    if (scaleFactor === undefined && image !== undefined && loadingScaling) {
      const sourceX = file.metadata.find(
        ({ key }) => key === DIMENSIONAL_METADATA_KEYS.x,
      )?.value;
      const sourceY = file.metadata.find(
        ({ key }) => key === DIMENSIONAL_METADATA_KEYS.y,
      )?.value;
      const sourceFileDimensions =
        typeof sourceX === "number" && typeof sourceY === "number"
          ? {
              x: sourceX,
              y: sourceY,
            }
          : null;

      if (isNullish(sourceFileDimensions)) {
        setLoadingScaling(false);
        return;
      }

      const scaleFactor = {
        x: sourceFileDimensions.x / image.width,
        y: sourceFileDimensions.y / image.height,
      };

      if (isNaN(scaleFactor.x) || isNaN(scaleFactor.y)) {
        setLoadingScaling(false);
        return;
      }

      setScaleFactor(scaleFactor);
      image.scaleX = scaleFactor.x;
      image.scaleY = scaleFactor.y;
      image.sourceFileDimensions = {
        width: sourceFileDimensions.x,
        height: sourceFileDimensions.y,
      };
      image.setCoords();

      setLoadingScaling(false);
    }
  }, [file.metadata, image, loadingScaling, scaleFactor]);

  if (error !== undefined) {
    return (
      <CallOutError
        message="Failed to load image/video from input file. This file has either not
          yet been processed (wait for it finish and retry) or is not supported
          by this analysis tool."
      />
    );
  }

  if (loading || loadingScaling) {
    return (
      <EuiEmptyPrompt
        icon={<EuiLoadingLogo logo="vector" size="xl" />}
        title={<h3>Loading...</h3>}
        titleSize="s"
        css={css`
          height: 100%;
          .euiEmptyPrompt__main {
            height: 100%;
          }
        `}
      />
    );
  }

  if (scaleFactor === undefined) {
    return <CallOutError message="Failed to read metadata from input file." />;
  }

  return (
    <RoiEditorProvider>
      <RoiEditor
        onCancel={onCancel}
        initialShapes={initialShapes}
        image={image}
        onAccept={onAccept}
        readOnly={readOnly}
        roiSettings={roiSettings}
      />
    </RoiEditorProvider>
  );
};
